import * as React from 'react';
import { scrollToAnchor, throttle, scrollToElement } from '@cian/newbuilding-utils';
import { ArticleHeading2 } from '@cian/ui-kit';
import { Tabs, type TTab } from '@cian/ui-kit/tabs';
import cx from 'clsx';
import * as styles from './Header.css';
import { collectClauses, getActiveTab, mapClausesToTabs } from './helpers';
import { getCurrentDocHeight } from './helpers/getCurrentDocHeight';
import { trackStickyHeaderTabClick } from './helpers/trackStickyHeaderTabClick';
import { ITabData } from './types';
import { STICKY_HEADER_HEIGHT } from '../../constants/elements';
import { getCurrentScrollOffset } from '../../utils/dom/getCurrentScrollOffset';
import { Layout } from '../Layout';

export interface IHeaderProps {
  name: string;
  isFixed: boolean;
  rating: React.ReactElement<unknown>;
  compare: React.ReactNode;
  call: React.ReactElement<unknown>;
  callback: React.ReactElement<unknown>;
  favorite: React.ReactElement<unknown>;
  fromDeveloperPropsCount: number;
  message: React.ReactNode;
}

export const Header = ({
  name,
  rating,
  isFixed,
  compare,
  call,
  callback,
  favorite,
  fromDeveloperPropsCount,
  message,
}: IHeaderProps) => {
  const [activeTab, setActiveTab] = React.useState<string>('start');
  const [tabs, setTabs] = React.useState<ITabData[]>([]);
  const [documentHeight, setDocumentHeight] = React.useState<number>(0);

  const updateActiveTab = (tabs: ITabData[]) => {
    const yOffset = getCurrentScrollOffset();
    const activeTab = getActiveTab(yOffset + 5, tabs);

    if (activeTab) {
      setActiveTab(activeTab.anchor);
    }
  };

  const updateTabData = () => {
    const clauses = collectClauses();
    const tabs = mapClausesToTabs(clauses);
    setTabs(tabs);
  };

  const checkDocHeight = (): boolean => {
    const currentHeight = getCurrentDocHeight();
    if (documentHeight !== currentHeight) {
      setDocumentHeight(currentHeight);

      return true;
    }

    return false;
  };

  const onNavigationLinkClick = React.useCallback((slug: string) => {
    trackStickyHeaderTabClick(slug);
    const anchor = `#${slug}`;
    if (slug === 'start') {
      scrollToElement({ selector: '#header-frontend' });
    } else {
      // Для ff и safari нужен запас в 10 для корректного определения активного таба
      scrollToAnchor({ anchor, hasRelocation: false, offset: -10 })(event);
    }
    setTimeout(() => {
      setActiveTab(slug);
    }, 605);
  }, []);

  const throttledScrollHandler = (tabs: ITabData[]) =>
    throttle(600, () => {
      if (checkDocHeight()) {
        updateTabData();
      }
      updateActiveTab(tabs);
    });

  const throttledResizeHandler = throttle(400, () => {
    updateTabData();
  });

  React.useEffect(() => {
    const anchor = window.location.hash;
    const slug = anchor.slice(1);
    const tabs = mapClausesToTabs(collectClauses());
    setDocumentHeight(getCurrentDocHeight());
    setTabs(tabs);
    if (slug) {
      setActiveTab(slug);
    }
    const scroll = throttledScrollHandler(tabs);
    window.addEventListener('scroll', scroll);
    window.addEventListener('resize', throttledResizeHandler);

    return () => {
      window.removeEventListener('scroll', scroll);
      window.removeEventListener('resize', throttledResizeHandler);
    };
  }, [documentHeight]);

  const activeTabIndex = Math.max(
    tabs.findIndex((tab: ITabData) => tab.anchor === activeTab),
    0,
  );

  const tabsItems = tabs.map<TTab>((tab: ITabData) => {
    const isFlatsTab = tab.anchor === 'filter';

    return {
      text: tab.title,
      counter: isFlatsTab ? fromDeveloperPropsCount : undefined,
      rightItem: isFlatsTab ? 'counter' : undefined,
    };
  });

  return (
    <div
      style={{ height: `${STICKY_HEADER_HEIGHT}px` }}
      className={cx(styles['header'], isFixed && styles['header--fixed'])}
      data-testid="StickyHeader"
    >
      <Layout stretch={true}>
        <div className={styles['content']}>
          <div className={styles['title']}>
            <div className={styles['rating']}>{rating}</div>

            <ArticleHeading2 whiteSpace={'nowrap'} as="div">
              {name}
            </ArticleHeading2>
            <div className={styles['title-controls']}>
              {compare}
              {favorite}
            </div>
          </div>
          <div className={styles['controls']}>
            {call}
            {message}
            {callback}
          </div>
        </div>
        <div className={styles['nav']}>
          <nav className={styles['tabs']}>
            {tabsItems.length > 0 && (
              <Tabs
                activeTabIndex={activeTabIndex}
                items={tabsItems}
                size="S"
                bottomDivider={false}
                onTabClick={(index: number) => {
                  const tab = tabs[index];

                  if (tab) {
                    onNavigationLinkClick(tab.anchor);
                  }
                }}
              />
            )}
          </nav>
        </div>
      </Layout>
    </div>
  );
};
